// src/lib/travelHealth/questionnairePrefill.ts
//
// Parses a submitted AHT Travel Health pre-consult questionnaire
// (stored as flat question/answer text pairs) back into the shape
// needed to pre-fill TravelHealthTier1Assessment. Pure functions only.
//
// IMPORTANT: fields not present in the responses array are left
// undefined here — the caller must NOT substitute a default or guess.
// Absence means "ask the pharmacist fresh," not "assume a value."

export interface QuestionnaireResponseItem {
  question: string;
  answer: string;
}

// Helper: find a response by exact question text
function findAnswer(
  responses: QuestionnaireResponseItem[],
  questionText: string,
): string | undefined {
  return responses.find((r) => r.question === questionText)?.answer;
}

// Helper: split a multi-select answer into its selected option labels
function splitMulti(answer: string | undefined): string[] {
  if (!answer) return [];
  return answer.split("; ").map((s) => s.trim()).filter(Boolean);
}

export interface QuestionnairePrefillResult {
  // Section 1 — About you
  fullName?: string;
  dateOfBirth?: string; // as submitted, DD/MM/YYYY text — caller may need to reformat
  sex?: "Male" | "Female" | "Non-binary" | "Prefer not to say";
  countryOfOrigin?: string;
  email?: string;
  phone?: string;
  homeAddress?: string;
  gpPracticeAndName?: string;
  weightKg?: string;

  // Section 2 — Trip
  tripDestinationsRaw?: string; // free text as submitted — see note below
  departureDate?: string;
  tripLengthDays?: string;
  accommodationType?:
    | "good-standard"
    | "basic-budget"
    | "camping-hostels"
    | "staying-with-family"
    | "mixed-not-sure";
  hasTravelInsurance?: "yes" | "no" | "not-yet";
  expectsToTravelAgain?: "yes" | "no" | "not-sure";
  tripTypes?: string[]; // option labels — caller maps to TripType values
  activities?: string[]; // option labels — caller maps to TravelActivity values
  needsPeriodDelay?: "yes" | "no" | "prefer-not-to-say";

  // Section 3 — Vaccination and treatment history
  priorVaccineNames?: string[]; // ticked option labels from Q19
  priorVaccineNotesRaw?: string; // Q20 — free text, NOT split per-vaccine
  priorAntimalarialTolerated?: "not-taken"; // only ever set when Q22 is "No" — never guessed otherwise
  priorAntimalarialNotesRaw?: string; // Q22a free text, surfaced as-is

  // Section 4 — Health
  // NOTE: Q24 "fit and well today" is deliberately excluded — see
  // constraints above. Do not add a field for it.
  allergies?: {
    egg: boolean;
    latex: boolean;
    neomycinOrAminoglycoside: boolean;
    formaldehyde: boolean;
    gelatinOrPolygeline: boolean;
    sulphonamides: boolean;
    aMedicine: boolean;
    aFood: boolean;
    aPreviousVaccine: boolean;
    noneKnown: boolean;
    other: boolean;
    detail: string;
  };
  isPregnant?: boolean;
  couldBePregnant?: boolean;
  isBreastfeeding?: boolean;
  // Note: "planning a pregnancy" is not a distinct option in the built
  // questionnaire's Q28 (only pregnant / could be / breastfeeding /
  // none / prefer not to say) — planningPregnancy is never set by this
  // parser. Flag this as a follow-up gap against the AHT_TH_01a source,
  // which does include it as a separate option.
  hasImmuneCondition?: boolean;
  immuneConditionDetail?: string;
  medicalHistory?: {
    anaemia: boolean;
    bleedingOrClottingDisorder: boolean;
    heartDiseaseOrHighBloodPressure: boolean;
    diabetes: boolean;
    epilepsyOrSeizures: boolean;
    stomachOrBowelProblems: boolean;
    liverOrKidneyProblems: boolean;
    hivAids: boolean;
    immuneSystemCondition: boolean;
    mentalHealthCondition: boolean;
    neurologicalIllness: boolean;
    respiratoryCondition: boolean;
    rheumatologyOrJointCondition: boolean;
    spleenProblemsOrNoSpleen: boolean;
    previousSurgeryIncludingSpleenOrThymus: boolean;
    kidneyStones: boolean;
    glaucoma: boolean;
    porphyria: boolean;
    additionalNeedOrDisability: boolean;
    // NOTE: the current medical history list (both in this questionnaire
    // and in Tier1Assessment's own checklist) does not include "FGM" as a
    // separate option, even though the original AHT_TH_01a source
    // document does. This is a pre-existing inconsistency in both
    // systems, not fixed here — flagging rather than adding it to only
    // one side.
    noneOfThese: boolean;
    other: boolean;
    otherDetail: string;
  };
  hivCd4Count?: string; // digits only, only set if the raw answer was purely numeric
  currentMedicinesRaw?: string; // free text, pass-through only

  // Items with no corresponding Tier1AssessmentData field today —
  // surfaced as labelled informational notes for the pharmacist to
  // read, not force-mapped into a field that doesn't exist
  additionalPatientNotes: { label: string; text: string }[];
}

export function parseQuestionnaireSections1And2(
  responses: QuestionnaireResponseItem[],
): QuestionnairePrefillResult {
  const result: QuestionnairePrefillResult = { additionalPatientNotes: [] };

  const fullName = findAnswer(responses, "What is your full name?");
  if (fullName !== undefined) result.fullName = fullName;

  const dob = findAnswer(responses, "What is your date of birth?");
  if (dob !== undefined) result.dateOfBirth = dob;

  const sex = findAnswer(responses, "What is your sex?");
  if (
    sex === "Male" ||
    sex === "Female" ||
    sex === "Non-binary" ||
    sex === "Prefer not to say"
  ) {
    result.sex = sex;
  }

  const country = findAnswer(responses, "Which country are you originally from?");
  if (country !== undefined) result.countryOfOrigin = country;

  const email = findAnswer(responses, "What is your email address?");
  if (email !== undefined) result.email = email;

  const phone = findAnswer(responses, "What is your mobile or telephone number?");
  if (phone !== undefined) result.phone = phone;

  const address = findAnswer(responses, "What is your home address?");
  if (address !== undefined) result.homeAddress = address;

  const gp = findAnswer(
    responses,
    "Which GP practice are you registered at and what is your GP name?",
  );
  if (gp !== undefined) result.gpPracticeAndName = gp;

  const weight = findAnswer(responses, "What is your weight (in kg)?");
  if (weight !== undefined) result.weightKg = weight;

  // Destinations: the questionnaire captures this as one free-text
  // block (country/region/urban-rural/dates all in one answer), but
  // Tier1AssessmentData.tripDestinations is string[] (one per
  // destination name). Do NOT attempt to parse structured destination
  // data out of free text — that risks silently dropping or
  // misreading a location. Surface the raw text for the pharmacist to
  // read and enter destinations manually against it.
  const destinationsRaw = findAnswer(
    responses,
    "Where are you going? Please list every country you are visiting, including any stopovers and onward travel. Include the region or exact location, whether each destination is urban or rural, and your dates for each leg.",
  );
  if (destinationsRaw !== undefined) result.tripDestinationsRaw = destinationsRaw;

  const departureDate = findAnswer(responses, "What date do you leave the UK?");
  if (departureDate !== undefined) result.departureDate = departureDate;

  const tripLength = findAnswer(responses, "How long is your whole trip in total?");
  if (tripLength !== undefined) result.tripLengthDays = tripLength;

  const accommodationAnswer = findAnswer(
    responses,
    "Where will you be staying, and what will food and water be like?",
  );
  const accommodationMap: Record<string, QuestionnairePrefillResult["accommodationType"]> = {
    "Good-standard accommodation": "good-standard",
    "Basic or budget accommodation": "basic-budget",
    "Camping or hostels": "camping-hostels",
    "Staying with a local family": "staying-with-family",
    "Mixed / not sure": "mixed-not-sure",
  };
  if (accommodationAnswer && accommodationMap[accommodationAnswer]) {
    result.accommodationType = accommodationMap[accommodationAnswer];
  }

  const insuranceAnswer = findAnswer(responses, "Do you have travel insurance?");
  if (insuranceAnswer === "Yes") result.hasTravelInsurance = "yes";
  else if (insuranceAnswer === "No") result.hasTravelInsurance = "no";
  else if (insuranceAnswer === "Not yet") result.hasTravelInsurance = "not-yet";

  const travelAgainAnswer = findAnswer(
    responses,
    "Do you expect to travel abroad again in the future?",
  );
  if (travelAgainAnswer === "Yes") result.expectsToTravelAgain = "yes";
  else if (travelAgainAnswer === "No") result.expectsToTravelAgain = "no";
  else if (travelAgainAnswer === "Not sure") result.expectsToTravelAgain = "not-sure";

  const tripTypesAnswer = findAnswer(
    responses,
    "What kind of trip is it? Tick everything that applies.",
  );
  const tripTypesList = splitMulti(tripTypesAnswer);
  if (tripTypesList.length > 0) result.tripTypes = tripTypesList;

  const activitiesAnswer = findAnswer(
    responses,
    "What will you be doing while you are away? Tick everything that applies.",
  );
  const activitiesList = splitMulti(activitiesAnswer);
  if (activitiesList.length > 0) result.activities = activitiesList;

  const periodDelayAnswer = findAnswer(
    responses,
    "Do you need to delay your period for any part of the trip?",
  );
  if (periodDelayAnswer === "Yes") result.needsPeriodDelay = "yes";
  else if (periodDelayAnswer === "No") result.needsPeriodDelay = "no";
  else if (periodDelayAnswer === "Prefer not to say") result.needsPeriodDelay = "prefer-not-to-say";

  return result;
}

export function parseQuestionnaireSections3And4(
  responses: QuestionnaireResponseItem[],
): Pick<
  QuestionnairePrefillResult,
  | "priorVaccineNames"
  | "priorVaccineNotesRaw"
  | "priorAntimalarialTolerated"
  | "priorAntimalarialNotesRaw"
  | "allergies"
  | "isPregnant"
  | "couldBePregnant"
  | "isBreastfeeding"
  | "hasImmuneCondition"
  | "immuneConditionDetail"
  | "medicalHistory"
  | "hivCd4Count"
  | "currentMedicinesRaw"
  | "additionalPatientNotes"
> {
  const notes: { label: string; text: string }[] = [];

  const priorVaccinesAnswer = findAnswer(
    responses,
    "Which of these vaccines have you had before? Tick all you can remember.",
  );
  const priorVaccineNames = splitMulti(priorVaccinesAnswer);

  const vaccineDatesRaw = findAnswer(
    responses,
    "If you can, add rough dates or any details for the vaccines you ticked above.",
  );

  const incompleteCourse = findAnswer(
    responses,
    "Have you ever started a vaccine course but not finished it? We continue where you left off rather than starting again.",
  );
  if (incompleteCourse === "Yes — please give details below") {
    const detail = findAnswer(
      responses,
      "Which vaccine course, and roughly when did you start it?",
    );
    if (detail) {
      notes.push({ label: "Incomplete vaccine course (from patient form)", text: detail });
    }
  }

  const malariaTabletsAnswer = findAnswer(
    responses,
    "Have you taken malaria tablets before? Did they suit you?",
  );
  let priorAntimalarialTolerated: "not-taken" | undefined;
  let priorAntimalarialNotesRaw: string | undefined;
  if (malariaTabletsAnswer === "No") {
    priorAntimalarialTolerated = "not-taken";
  } else if (
    malariaTabletsAnswer ===
    "Yes — which tablets and how you got on (please give details below)"
  ) {
    const detail = findAnswer(
      responses,
      "Which malaria tablets did you take, and how did you get on with them?",
    );
    if (detail) priorAntimalarialNotesRaw = detail;
    // Deliberately NOT setting priorAntimalarialTolerated to "yes" or
    // "no" here — the free-text detail may or may not indicate
    // tolerance clearly, and guessing risks a wrong clinical value.
    // Leave it unset so the pharmacist confirms directly.
  }

  const certificateAnswer = findAnswer(
    responses,
    "Do you already have a vaccination certificate, such as a yellow fever certificate (ICVP)?",
  );
  if (certificateAnswer === "Yes — please give details and date below") {
    const detail = findAnswer(
      responses,
      "Please give details of your certificate, including the date it was issued.",
    );
    if (detail) {
      notes.push({ label: "Existing vaccination certificate (from patient form)", text: detail });
    }
  }

  // ── Allergies (Q25) ──────────────────────────────────────────
  const allergyAnswer = findAnswer(
    responses,
    "Do you have any allergies? Tick anything that applies.",
  );
  const allergyLabels = splitMulti(allergyAnswer);
  let allergies: QuestionnairePrefillResult["allergies"] | undefined;
  if (allergyLabels.length > 0) {
    const detail = findAnswer(
      responses,
      "If you ticked a medicine, food, previous vaccine, or 'other' above, please give details (including any reaction).",
    ) ?? "";
    allergies = {
      egg: allergyLabels.includes("Egg"),
      latex: allergyLabels.includes("Latex"),
      neomycinOrAminoglycoside: allergyLabels.includes(
        "Neomycin or other aminoglycoside antibiotics",
      ),
      formaldehyde: allergyLabels.includes("Formaldehyde"),
      gelatinOrPolygeline: allergyLabels.includes("Gelatin / polygeline"),
      sulphonamides: allergyLabels.includes(
        "Sulphonamides (\"sulfa\" antibiotics or water tablets)",
      ),
      aMedicine: allergyLabels.includes("A medicine — please give details below"),
      aFood: allergyLabels.includes("A food — please give details below"),
      aPreviousVaccine: allergyLabels.includes(
        "A previous vaccine — please give details below",
      ),
      noneKnown: allergyLabels.includes("None that I know of"),
      other: allergyLabels.includes("Other — please give details below"),
      detail,
    };
  }

  const severeReaction = findAnswer(
    responses,
    "Have you, or has anyone in your immediate family, had a severe allergic reaction or anaphylaxis?",
  );
  if (severeReaction === "Yes — please give details below") {
    const detail = findAnswer(responses, "Please give details of the reaction.");
    if (detail) {
      notes.push({ label: "Severe allergic reaction history (from patient form)", text: detail });
    }
  }

  const faints = findAnswer(
    responses,
    "Do you faint or feel faint with injections or blood tests?",
  );
  if (faints === "Yes") {
    notes.push({
      label: "Fainting with injections (from patient form)",
      text: "Patient reported they faint or feel faint with injections/blood tests.",
    });
  }

  // ── Pregnancy (Q28) ──────────────────────────────────────────
  const pregnancyAnswer = findAnswer(
    responses,
    "Are you pregnant, could you be pregnant, or are you breastfeeding?",
  );
  let isPregnant: boolean | undefined;
  let couldBePregnant: boolean | undefined;
  let isBreastfeeding: boolean | undefined;
  if (pregnancyAnswer === "No — none of these apply") {
    isPregnant = false;
    couldBePregnant = false;
    isBreastfeeding = false;
  } else if (pregnancyAnswer === "I am pregnant") {
    isPregnant = true;
  } else if (pregnancyAnswer === "I could be pregnant") {
    couldBePregnant = true;
  } else if (pregnancyAnswer === "I am breastfeeding") {
    isBreastfeeding = true;
  }
  // "Prefer not to say" and absent answers leave all three undefined —
  // never treated as "no".

  // ── Immune condition (Q29) ───────────────────────────────────
  const immuneAnswer = findAnswer(
    responses,
    "Do you have any condition or take any medicine that affects your immune system? This includes high-dose steroids, biologic medicines, chemotherapy or radiotherapy, an organ transplant, or HIV. (Spleen problems are asked about separately.)",
  );
  let hasImmuneCondition: boolean | undefined;
  let immuneConditionDetail: string | undefined;
  if (immuneAnswer === "No") {
    hasImmuneCondition = false;
  } else if (immuneAnswer === "Yes — please give details below") {
    hasImmuneCondition = true;
    immuneConditionDetail = findAnswer(
      responses,
      "Please give details of the condition or medicine.",
    );
  }

  const cd4 = findAnswer(
    responses,
    "If you have HIV, do you know your most recent CD4 count?",
  );
  if (cd4) {
    notes.push({ label: "Reported HIV CD4 count (from patient form)", text: cd4 });
  }
  const cd4NumericOnly = cd4?.trim().match(/^\d{1,4}$/) ? cd4.trim() : undefined;

  // ── Medicines ─────────────────────────────────────────────────
  const currentMedicinesRaw = findAnswer(
    responses,
    "What medicines do you take? Please include prescription medicines, anything you buy over the counter, the contraceptive pill, and herbal remedies such as St John's Wort.",
  );

  // ── Medical history (Q31/32) ─────────────────────────────────
  const medicalHistoryAnswer = findAnswer(
    responses,
    "Do any of these apply to you, now or in the past? Tick all that apply.",
  );
  const conditionLabels = splitMulti(medicalHistoryAnswer);
  let medicalHistory: QuestionnairePrefillResult["medicalHistory"] | undefined;
  if (conditionLabels.length > 0) {
    const otherDetail = findAnswer(
      responses,
      "Please give details of the other condition you ticked above.",
    ) ?? "";
    medicalHistory = {
      anaemia: conditionLabels.includes("Anaemia"),
      bleedingOrClottingDisorder: conditionLabels.includes(
        "Bleeding or clotting disorder (including DVT / blood clots)",
      ),
      heartDiseaseOrHighBloodPressure: conditionLabels.includes(
        "Heart disease or high blood pressure",
      ),
      diabetes: conditionLabels.includes("Diabetes"),
      epilepsyOrSeizures: conditionLabels.includes(
        "Epilepsy or seizures (you or a close relative)",
      ),
      stomachOrBowelProblems: conditionLabels.includes("Stomach or bowel problems"),
      liverOrKidneyProblems: conditionLabels.includes("Liver or kidney problems"),
      hivAids: conditionLabels.includes("HIV / AIDS"),
      immuneSystemCondition: conditionLabels.includes(
        "An immune-system condition (e.g. a blood cancer)",
      ),
      mentalHealthCondition: conditionLabels.includes(
        "A mental health condition — anxiety or depression, now or in the past",
      ),
      neurologicalIllness: conditionLabels.includes(
        "A neurological (nervous system) illness",
      ),
      respiratoryCondition: conditionLabels.includes("A respiratory (breathing) condition"),
      rheumatologyOrJointCondition: conditionLabels.includes(
        "A rheumatology or joint condition",
      ),
      spleenProblemsOrNoSpleen: conditionLabels.includes("Spleen problems, or no spleen"),
      previousSurgeryIncludingSpleenOrThymus: conditionLabels.includes(
        "Previous surgery (including spleen or thymus removal)",
      ),
      kidneyStones: conditionLabels.includes("Kidney stones"),
      glaucoma: conditionLabels.includes("Glaucoma"),
      porphyria: conditionLabels.includes("Porphyria"),
      additionalNeedOrDisability: conditionLabels.includes(
        "An additional need or disability",
      ),
      noneOfThese: conditionLabels.includes("None of these"),
      other: conditionLabels.includes("Other — please give details below"),
      otherDetail,
    };
  }

  const anythingElse = findAnswer(
    responses,
    "Is there anything else you would like your clinician to know?",
  );
  if (anythingElse) {
    notes.push({ label: "Anything else (from patient form)", text: anythingElse });
  }

  return {
    priorVaccineNames: priorVaccineNames.length > 0 ? priorVaccineNames : undefined,
    priorVaccineNotesRaw: vaccineDatesRaw,
    priorAntimalarialTolerated,
    priorAntimalarialNotesRaw,
    allergies,
    isPregnant,
    couldBePregnant,
    isBreastfeeding,
    hasImmuneCondition,
    immuneConditionDetail,
    medicalHistory,
    hivCd4Count: cd4NumericOnly,
    currentMedicinesRaw,
    additionalPatientNotes: notes,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// DATE HELPERS
//
// The pre-consult form captures the departure date and trip length as
// free text, not as validated date inputs. These helpers only convert
// text that unambiguously matches the UK date convention used
// throughout this app (DD/MM/YYYY) or a plain "N days" trip length.
// Anything else — different formats, month names, ranges — returns
// undefined rather than guessing, so the caller must fall back to
// asking the pharmacist to enter it manually.
// ─────────────────────────────────────────────────────────────────────────────

/** Converts a strict DD/MM/YYYY string to ISO (YYYY-MM-DD). Returns
 *  undefined if the text doesn't match that exact pattern or describes
 *  an invalid calendar date. */
export function parseUkDateToIso(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const match = raw.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return undefined;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  if (month < 1 || month > 12) return undefined;
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day < 1 || day > daysInMonth) return undefined;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Extracts a whole number of days from a strict "N day(s)" answer.
 *  Returns undefined for anything else (ranges, weeks, free text). */
export function parseTripLengthDays(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  const match = raw.trim().match(/^(\d+)\s*days?$/i);
  if (!match) return undefined;
  return Number(match[1]);
}

/** Adds a number of days to an ISO date and returns the result as ISO. */
export function addDaysToIsoDate(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

// ─────────────────────────────────────────────────────────────────────────────
// DESTINATION HELPER
//
// The pre-consult form captures destinations as free text (e.g.
// "Kenya — Nairobi (urban), 10–15 Aug; then a rural safari region").
// Rather than guess at a structured breakdown, this only recognises
// exact country names from the app's known country list, in the order
// they first appear. Anything else in the text (regions, urban/rural
// detail, per-leg dates) is not captured here — it stays visible as
// raw text for the pharmacist to review alongside the matches.
// ─────────────────────────────────────────────────────────────────────────────

/** Finds known country names mentioned in free text, longest names
 *  first so e.g. "Papua New Guinea" isn't also partially matched as
 *  "Guinea". Returns matches in the order they appear in the text. */
export function extractKnownCountriesFromText(
  raw: string | undefined,
  knownCountries: string[],
): string[] {
  if (!raw) return [];
  const sortedByLengthDesc = [...knownCountries].sort(
    (a, b) => b.length - a.length,
  );
  let workingText = raw;
  const matches: { name: string; index: number }[] = [];
  for (const country of sortedByLengthDesc) {
    const escaped = country.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const pattern = new RegExp(`\\b${escaped}\\b`, "i");
    const match = workingText.match(pattern);
    if (match && match.index !== undefined) {
      matches.push({ name: country, index: match.index });
      workingText =
        workingText.slice(0, match.index) +
        " ".repeat(match[0].length) +
        workingText.slice(match.index + match[0].length);
    }
  }
  return matches.sort((a, b) => a.index - b.index).map((m) => m.name);
}
